import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetCompanyEmployeesQuery,
  useGetCompanyJobsQuery,
  useGetCompanyQuery,
} from "@diditusa/shared/api/companyApi";
import LoadingIndicator from "@diditusa/shared/web_components/LoadingIndicator/loadingIndicator";
import { Box, Button, Typography, Grid, Avatar, Divider } from "@mui/material";
import {
  BasicTableHeader,
  CompanyRole,
} from "@diditusa/shared/types/commonTypes";
import { JobStatus } from "@diditusa/shared/types/jobTypes";

import { format } from "date-fns";
import BasicTable from "@diditusa/shared/web_components/BasicTable/BasicTable";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import { DateTime } from "luxon";

const CompaniesDetails = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const {
    data: company,
    isLoading: isCompanyLoading,
    isError: isCompanyError,
    error: companyError,
  } = useGetCompanyQuery(parseInt(companyId!));

  const {
    data: employees,
    isLoading: isEmployeesLoading,
    isError: isEmployeesError,
    error: employeesError,
  } = useGetCompanyEmployeesQuery(parseInt(companyId!));

  const {
    data: jobs,
    isLoading: isJobsLoading,
    isError: isJobsError,
    error: jobsError,
  } = useGetCompanyJobsQuery(parseInt(companyId!));

  const renderJobs = () => {
    if (isJobsLoading) {
      return <LoadingIndicator isVisible={isJobsLoading} />;
    }

    if (isJobsError) {
      return <p>Error loading employees: {jobsError.toString()}</p>;
    }

    // const completed = jobs?.filter(
    //   (j) => j.status.toString() === JobStatus[JobStatus.COMPLETED]
    // );
    // const nonCompleted = jobs?.filter(
    //   (j) => j.status.toString() !== JobStatus[JobStatus.COMPLETED]

    // );
    interface JobDetails {
      id: number;
      reservationCode: string;
      user: string;
      location: string;
      agents: string;
      startDate: DateTime;
      startTime: DateTime;
      duration: number;
      package: string;
      status: JobStatus;
    }

    type JobDetailsKey = keyof JobDetails;
    const jobDetails: JobDetails[] =
      jobs?.map((j) => ({
        id: j.id,
        reservationCode: j.reservationCode,
        user: j.createdBy
          ? `${j.createdBy.firstName} ${j.createdBy.lastName}`
          : j.createdById,
        agents:
          j.jobAssignments
            ?.map((ja) =>
              ja.user ? `${ja.user.firstName} ${ja.user.lastName}` : ja.userId
            )
            .join(",") || "",
        startTime: j.startTime,
        startDate: j.startTime,
        duration: j.durationHours,
        package: j.securityOption?.title || "",
        location: j.location,
        status: j.status,
      })) || [];

    const headers: BasicTableHeader<JobDetailsKey>[] = [
      { key: "reservationCode", value: "Reservation Number" },
      { key: "user", value: "Customer" },
      { key: "agents", value: "Assigned Agent" },
      { key: "location", value: "Location" },
      { key: "startDate", value: "Start Date" },
      { key: "startTime", value: "Start Time" },
      { key: "duration", value: "Duration" },
      { key: "package", value: "Package" },
      { key: "status", value: "Status" },
    ];

    const getCustomComponentForCell = (field: JobDetailsKey, idx: number) => {
      const c = jobDetails[idx];

      if (field === "startDate") {
        return DateTime.fromISO(String(c.startDate)).toFormat("MM/dd/yyyy");
      }

      if (field === "startTime") {
        return DateTime.fromISO(String(c.startTime)).toFormat("hh:mm a ZZZZ");
      }
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" mt={4} mb={2}>
          Assigned Jobs
        </Typography>
        <BasicTable
          headers={headers}
          data={jobDetails}
          getCustomComponentForField={getCustomComponentForCell}
        />
      </Box>
    );
  };

  const renderEmployees = () => {
    if (isEmployeesLoading) {
      return <LoadingIndicator isVisible={isEmployeesLoading} />;
    }

    if (isEmployeesError) {
      return <p>Error loading employees: {employeesError.toString()}</p>;
    }

    interface EmployeeDetails {
      id: string;
      imageUrl?: string;
      name: string;
      email: string;
      phoneNumber?: string;
      companyRole: CompanyRole;
      createdAt: Date;
      edit: () => void;
    }

    type EmployeeDetailsKey = keyof EmployeeDetails;
    const employeeDetails: EmployeeDetails[] =
      employees?.map((e) => ({
        id: e.id!,
        name: `${e.firstName} ${e.lastName}`,
        email: e.email!,
        phoneNumber: e.phoneNumber,
        companyRole: e.companyRole!,
        createdAt: e.createdAt!,
        imageUrl: e.imageUrl,
        edit: () => {
          navigate(`agents/${e.id}/edit`);
        },
      })) || [];

    const headers: BasicTableHeader<EmployeeDetailsKey>[] = [
      { key: "imageUrl", value: "" },
      { key: "name", value: "Name" },
      { key: "email", value: "Email" },
      { key: "phoneNumber", value: "Phone Number" },
      { key: "companyRole", value: "Role" },
      { key: "createdAt", value: "Joined" },
      { key: "edit", value: "" },
    ];

    const getCustomComponentForCell = (
      field: EmployeeDetailsKey,
      idx: number
    ) => {
      const c = employeeDetails[idx];
      if (field === "imageUrl") {
        return c.imageUrl ? (
          <Avatar src={c.imageUrl} alt={c.name} sx={{ mr: 2 }} />
        ) : (
          <CorporateFareIcon />
        );
      }

      if (field === "phoneNumber") {
        return (
          <Button variant="outlined" sx={{ borderRadius: 5 }}>
            {c.phoneNumber}
          </Button>
        );
      }

      if (field === "edit") {
        return <Button onClick={c.edit}>Edit</Button>;
      }
    };

    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" mt={4} mb={2}>
          Company Employees
        </Typography>
        <BasicTable
          headers={headers}
          data={employeeDetails}
          getCustomComponentForField={getCustomComponentForCell}
        />
      </Box>
    );
  };

  if (isCompanyLoading) {
    return <LoadingIndicator isVisible={isCompanyLoading} />;
  }

  if (isCompanyError) {
    return <p>Error loading company: {(companyError as any)?.message}</p>;
  }

  return (
    <Box display="flex" flexGrow={1} flexDirection={"column"}>
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        mb={2}
        flexGrow={1}
        flexDirection={"column"}
      >
        <Avatar
          variant="rounded"
          sx={{ width: 128, height: 128 }}
          src="https://cdn.usegalileo.ai/sdxl10/9078f489-f174-4711-a673-26cf1cf2aea2.png"
        />
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {company?.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Security Solutions Provider
          </Typography>
        </Box>
      </Box>
      <Divider />

      {/* <Box display="flex" gap={2} mb={3}>
          <Button variant="contained" color="primary">
            Invite New Guard
          </Button>
          <Button variant="outlined" color="inherit">
            Company Metadata
          </Button>
        </Box> */}

      <Grid container spacing={2} flexGrow={1}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Address
          </Typography>
          <Typography>{company?.address || "-"}</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Phone Number
          </Typography>
          <Typography>{company?.phoneNumber || "-"}</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Email
          </Typography>
          <Typography>{company?.contactEmail || "-"}</Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Licenses
          </Typography>
          <Typography>{company?.licenses?.join(",") || "-"}</Typography>
        </Grid>
      </Grid>
      <Divider />
      {renderJobs()}

      {renderEmployees()}
      {/* <Box
        display="flex"
        sx={{
          padding: "4px",
          marginBottom: "4px",
          textAlign: "left",
        }}
      >
        <Box
          component="img"
          src="/buildingPlaceholder.png"
          width="185px"
          height="150px"
        ></Box>
        <Box display="flex" flexDirection="column" margin="8">
          <Typography variant="h3" component="div">
            {company?.name}
          </Typography>
          <Typography variant="h6" component="div">
            {company?.contactEmail}
          </Typography>
          <Typography variant="h6" component="div">
            {company?.address}
          </Typography>
        </Box>
        <Button onClick={onAddEmployeesClick}>Add employees</Button>
        <Box>
          <LoadingIndicator isVisible={inviteUsersToCompanyState.isLoading} />
          <Input
            type="text"
            value={inviteName}
            placeholder={"Name"}
            onChange={(e) => setInviteName(e.currentTarget.value)}
          />
          <Input
            type="text"
            value={inviteEmail}
            placeholder={"Email"}
            onChange={(e) => setInviteEmail(e.currentTarget.value)}
          />
          <Button onClick={onSendInviteClick}>Send Invite</Button>
        </Box>
      </Box>
      {renderJobs()}
      {renderEmployees()}
      <AddEmployeesModal
        onClose={onCloseInviteUserModal}
        shouldShow={showInviteUserModal}
        isAllUsersError={isAllUsersError}
        isAllUsersLoading={isAllUsersLoading}
        allAppUsers={allAppUsers || []}
        allUsersError={allUsersError}
        onAddClick={onAddSelectedEmployees}
        isAddEmployeesLoading={isAddEmployeesLoading}
        isAddEmployeesError={isAddEmployeesError}
        addEmployeesError={addEmployeesError}
      />
      {renderDeleteEmployeeConfirmationModal()} */}
    </Box>
  );
};

export default CompaniesDetails;
